<template>
  <div class="user-detail-page">
    <div v-if="loading" class="text-center mt-3">
      <circle-loader :size="40"></circle-loader>
    </div>
    <div v-if="!loading && user">
      <div class="user-info mb-4">
        <h5>User Information</h5>
        <div class="info-grid">
          <div class="info-item"><strong>Email:</strong> {{ user.email }}</div>
          <div class="info-item">
            <strong>Phone:</strong> {{ user.phone || 'N/A' }}
          </div>
          <div class="info-item">
            <strong>Name:</strong>
            {{ user.firstName }} {{ user.lastName }}
          </div>
          <div class="info-item"><strong>Type:</strong> {{ user.type }}</div>
          <div class="info-item">
            <strong>Department:</strong> {{ user.department || 'N/A' }}
          </div>
          <div class="info-item">
            <strong>Communication Preference:</strong>
            {{ user.communicationPreference }}
          </div>
          <div class="info-item" v-if="user.address && user.address.street1">
            <strong>Address:</strong>
            <div>{{ user.address.street1 }}</div>
            <div v-if="user.address.street2">{{ user.address.street2 }}</div>
            <div>
              {{ user.address.city }}, {{ user.address.state }}
              {{ user.address.zip }}
            </div>
          </div>
          <div class="info-item">
            <strong>Created:</strong> {{ formatDate(user.createdAt) }}
          </div>
        </div>
      </div>
      <div class="actions mb-4">
        <b-button variant="primary" @click="$bvModal.show('edit-user-modal')">
          Edit User
        </b-button>
        <b-button
          variant="danger"
          @click="$bvModal.show('delete-user-modal')"
          class="ml-2"
        >
          Delete User
        </b-button>
      </div>

      <!-- Edit User Modal -->
      <b-modal
        id="edit-user-modal"
        :title="`Edit User - ${user.email}`"
        hide-footer
        size="xl"
      >
        <form @submit.prevent="submitUpdateUser">
          <user-form-fields
            :submitting="submitting"
            :user="editUser"
            :password.sync="editPassword"
          ></user-form-fields>
          <div class="modal-ctas mt-4 text-right">
            <b-button
              variant="secondary"
              @click="$bvModal.hide('edit-user-modal')"
              :disabled="submitting"
              class="mr-2"
              >Cancel</b-button
            >
            <b-button variant="primary" type="submit" :disabled="submitting">
              <span v-if="submitting">Saving...</span>
              <span v-if="!submitting">Save Changes</span>
            </b-button>
          </div>
        </form>
      </b-modal>

      <!-- Delete User Modal -->
      <b-modal id="delete-user-modal" title="Delete User" hide-footer>
        <p>Are you sure you want to delete this user?</p>
        <p>
          <strong>{{ user.email }}</strong>
        </p>
        <p class="text-danger">This action cannot be undone.</p>
        <div class="modal-ctas text-right">
          <b-button
            variant="secondary"
            @click="$bvModal.hide('delete-user-modal')"
            :disabled="submitting"
            class="mr-2"
            >Cancel</b-button
          >
          <b-button
            variant="danger"
            @click="submitDeleteUser"
            :disabled="submitting"
          >
            <span v-if="submitting">Deleting...</span>
            <span v-if="!submitting">Delete User</span>
          </b-button>
        </div>
      </b-modal>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import axios from 'axios'
import moment from 'moment'
import CircleLoader from '@/components/CircleLoader.vue'
import UserFormFields from '@/components/UserFormFields.vue'
import { userStore } from '@/stores/userStore'

export default {
  name: 'UserDetailPage',
  components: { CircleLoader, UserFormFields },
  inject: { userStore },
  props: {
    userId: {
      type: String,
      required: true,
    },
  },
  data: function () {
    return {
      loading: true,
      submitting: false,
      user: null,
      editUser: null,
      editPassword: '',
    }
  },
  methods: {
    formatDate(date) {
      return moment(date).format('MMM D, YYYY h:mm A')
    },
    getUser() {
      this.loading = true
      return axios
        .get(`/services/v2/users/${this.userId}`)
        .then(response => {
          this.user = response.data.user
          this.prepareEditUser()
        })
        .catch(err => {
          console.log('err', err)
          this.$toasted.error('Error loading user', {
            duration: 4000,
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    prepareEditUser() {
      this.editUser = _.cloneDeep(this.user)
      if (!this.editUser.address) {
        this.editUser.address = {
          street1: '',
          street2: '',
          city: '',
          state: '',
          zip: '',
          country: '',
        }
      }
      this.editPassword = ''
    },
    submitUpdateUser() {
      this.submitting = true
      const data = _.clone(this.editUser)
      if (this.editPassword) {
        data.password = this.editPassword
      }
      return axios
        .patch(`/services/v2/users/${this.userId}`, data)
        .then(response => {
          this.user = response.data.user
          this.$bvModal.hide('edit-user-modal')
          this.$emit('user-updated', response.data.user)
          return this.$toasted.success('User Updated', {
            duration: 4000,
          })
        })
        .catch(err => {
          return this.$toasted.error(
            'Error updating user: ' + err?.response?.data?.error,
            {
              duration: 4000,
            },
          )
        })
        .finally(() => {
          this.submitting = false
        })
    },
    submitDeleteUser() {
      this.submitting = true
      return axios
        .delete(`/services/v2/users/${this.userId}`)
        .then(response => {
          this.$bvModal.hide('delete-user-modal')
          this.$emit('user-deleted', this.userId)
          return this.$toasted.success('User Deleted', {
            duration: 4000,
          })
        })
        .catch(err => {
          return this.$toasted.error(
            'Error deleting user: ' + err?.response?.data?.error,
            {
              duration: 4000,
            },
          )
        })
        .finally(() => {
          this.submitting = false
        })
    },
  },
  mounted() {
    this.getUser()
  },
  watch: {
    userId(newVal) {
      if (newVal) {
        this.getUser()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.info-item {
  padding: 8px;
  background-color: #f8f9fa;
  border-radius: 4px;

  strong {
    display: block;
    margin-bottom: 4px;
  }
}

.actions {
  display: flex;
  gap: 8px;
}
</style>


